import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import { 
    fetchBoardTemplates, 
    fetchBoardTemplate,
    createBoardTemplate,
    updateBoardTemplate,
    deleteBoardTemplate,
    reactivateBoardTemplate
} from '@/services/boardTemplateService';
import * as kanbanBoardService from '@/services/kanbanBoardService';
import '@/styles/TemplateManagement.scss';
import { FaEdit, FaTrash, FaSpinner, FaLayerGroup, FaPlus, FaInbox, FaFolder, FaEye, FaTimes, FaSave, FaPlay, FaCog, FaUsers, FaCopy, FaClipboardList, FaExchangeAlt, FaArrowRight, FaTasks, FaFilter, FaSearch, FaSyncAlt } from 'react-icons/fa';
import { IoPeople, IoBusiness, IoPerson } from 'react-icons/io5';
import IconPickerGrid, { DEFAULT_OPTIONS as ICON_PICKER_OPTIONS } from '@/components/Common/IconPickerGrid';
import CreateBoardModal from '@/pages/ActionItems/components/CreateBoardModal';
import BoardSettingsModal from '@/pages/ActionItems/components/BoardSettingsModal';
import ManageAccessModal from '@/pages/ActionItems/components/ManageAccessModal';
import DuplicateBoardModal from '@/pages/ActionItems/components/DuplicateBoardModal';
import { FaIcon } from '@/utils/faIconMap';
import * as Sentry from '@sentry/react';

/** Lookup map: icon value -> React Icon component for rendering stored icon strings */
const ICON_RESOLVE_MAP = Object.fromEntries(ICON_PICKER_OPTIONS.map(o => [o.value, o.Icon]));

/**
 * Board Template Management Page
 * Admin interface for managing Kanban Board templates
 * Allows creating, editing, activating/deactivating, and deleting board templates
 */
const BoardTemplateManagementPage = () => {
    const { apiFetch } = useAuth();

    // Tab state
    const [activeTab, setActiveTab] = useState('templates');

    // ── Template tab state ──────────────────────────────────────────────────
    const [groupedTemplates, setGroupedTemplates] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [showEditor, setShowEditor] = useState(false);
    const [filterCategory, setFilterCategory] = useState('All');
    const [showInactive, setShowInactive] = useState(false);
    const [categories, setCategories] = useState(['All']);
    const [confirmDelete, setConfirmDelete] = useState(null);

    // ── Board Management tab state ─────────────────────────────────────────
    const [boards, setBoards] = useState([]);
    const [loadingBoards, setLoadingBoards] = useState(false);
    const [boardSearchQuery, setBoardSearchQuery] = useState('');
    const [boardStatusFilter, setBoardStatusFilter] = useState('all');
    const [showCreateBoardModal, setShowCreateBoardModal] = useState(false);
    const [showBoardSettingsModal, setShowBoardSettingsModal] = useState(false);
    const [showManageAccessModal, setShowManageAccessModal] = useState(false);
    const [showDuplicateBoardModal, setShowDuplicateBoardModal] = useState(false);
    const [confirmDeleteBoard, setConfirmDeleteBoard] = useState(null);
    const [currentBoard, setCurrentBoard] = useState(null);
    
    /**
     * Group templates by category for organized display
     */
    const groupTemplatesByCategory = useCallback((templateList) => {
        const grouped = templateList.reduce((acc, template) => {
            const cat = template.category || 'Uncategorized';
            if (!acc[cat]) {
                acc[cat] = [];
            }
            acc[cat].push(template);
            return acc;
        }, {});

        // Extract unique categories for filter dropdown
        const uniqueCategories = ['All', ...Object.keys(grouped).sort()];
        setCategories(uniqueCategories);
        setGroupedTemplates(grouped);
    }, []);

    /**
     * Load all board templates from the API
     */
    const loadTemplates = useCallback(async () => {
        try {
            setLoading(true);
            const data = await fetchBoardTemplates(apiFetch, { includeInactive: showInactive });
            groupTemplatesByCategory(data);
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error loading board templates:', error);
            toast.error('Error loading templates: ' + error.message);
        } finally {
            setLoading(false);
        }
    }, [apiFetch, showInactive, groupTemplatesByCategory]);

    useEffect(() => {
        (async () => { await loadTemplates(); })();
    }, [loadTemplates]);

    // ── Board Management tab logic ─────────────────────────────────────────
    const loadBoards = useCallback(async () => {
        try {
            setLoadingBoards(true);
            const data = await kanbanBoardService.fetchAllBoardsAdmin(apiFetch);
            setBoards(data);
        } catch {
            // Fallback to regular boards endpoint if admin endpoint fails
            try {
                const data = await kanbanBoardService.fetchBoards(apiFetch);
                setBoards(data);
            } catch (fallbackError) {
                Sentry.captureException(fallbackError);
                toast.error('Failed to load boards');
            }
        } finally {
            setLoadingBoards(false);
        }
    }, [apiFetch]);

    useEffect(() => {
        if (activeTab === 'boards' || activeTab === 'tasks') {
            (async () => { await loadBoards(); })();
        }
    }, [activeTab, loadBoards]);

    const handleDeleteBoard = async (boardId) => {
        try {
            await kanbanBoardService.deleteBoard(apiFetch, boardId);
            toast.success('Board deleted successfully');
            setConfirmDeleteBoard(null);
            await loadBoards();
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to delete board');
        }
    };

    const filteredBoards = boards.filter(board => {
        const matchesSearch = board.name.toLowerCase().includes(boardSearchQuery.toLowerCase()) ||
            board.description?.toLowerCase().includes(boardSearchQuery.toLowerCase());
        const matchesStatus = boardStatusFilter === 'all' ||
            (boardStatusFilter === 'active' && board.isActive) ||
            (boardStatusFilter === 'inactive' && !board.isActive);
        return matchesSearch && matchesStatus;
    });

    /**
     * Filter templates based on selected category
     */
    const getFilteredTemplates = () => {
        if (filterCategory === 'All') {
            return groupedTemplates;
        }
        return {
            [filterCategory]: groupedTemplates[filterCategory] || []
        };
    };

    /**
     * Handle template deletion - show confirmation dialog
     */
    const handleDeleteClick = (templateId, templateName) => {
        setConfirmDelete({ templateId, templateName });
    };

    /**
     * Confirm and execute template deletion
     */
    const handleDeleteConfirm = async () => {
        if (!confirmDelete) return;

        try {
            await deleteBoardTemplate(apiFetch, confirmDelete.templateId);
            toast.success('Template deleted successfully');
            loadTemplates();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error deleting template:', error);
            toast.error(error.message || 'Failed to delete template');
        } finally {
            setConfirmDelete(null);
        }
    };

    /**
     * Cancel template deletion
     */
    const handleDeleteCancel = () => {
        setConfirmDelete(null);
    };

    /**
     * Handle template reactivation
     */
    const handleReactivate = async (templateId, templateName) => {
        try {
            await reactivateBoardTemplate(apiFetch, templateId);
            toast.success(`Template "${templateName}" reactivated successfully`);
            loadTemplates();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error reactivating template:', error);
            toast.error(error.message || 'Failed to reactivate template');
        }
    };

    /**
     * Handle template edit - opens the editor with the selected template
     */
    const handleEdit = async (templateId) => {
        try {
            const templateData = await fetchBoardTemplate(apiFetch, templateId);
            setSelectedTemplate(templateData);
            setShowEditor(true);
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error loading template details:', error);
            toast.error('Failed to load template details');
        }
    };

    /**
     * Handle template preview
     */
    const handlePreview = async (templateId) => {
        try {
            const templateData = await fetchBoardTemplate(apiFetch, templateId);
            setSelectedTemplate({ ...templateData, previewMode: true });
            setShowEditor(true);
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error loading template details:', error);
            toast.error('Failed to load template details');
        }
    };

    /**
     * Handle creating new template
     */
    const handleCreateNew = () => {
        setSelectedTemplate(null);
        setShowEditor(true);
    };

    /**
     * Handle closing the editor
     */
    const handleCloseEditor = () => {
        setShowEditor(false);
        setSelectedTemplate(null);
    };

    /**
     * Render template card
     */
    const renderTemplateCard = (template) => {
        const columnCount = template.columns?.length || 0;
        
        return (
            <div 
                key={template.templateId} 
                className={`template-card ${!template.isActive ? 'inactive' : ''}`}
                style={{ borderLeftColor: template.color || '#6c757d' }}
            >
                <div className="template-header">
                    <div className="template-icon" style={{ color: template.color || '#6c757d' }}>
                        {(() => {
                            const ResolvedIcon = template.icon && ICON_RESOLVE_MAP[template.icon];
                            return ResolvedIcon ? <ResolvedIcon size={24} /> : (template.icon || '📋');
                        })()}
                    </div>
                    <div className="template-title">
                        <h4>{template.name}</h4>
                        <span className="template-meta-text">
                            {columnCount} column{columnCount !== 1 ? 's' : ''} • 
                            Used {template.usageCount} time{template.usageCount !== 1 ? 's' : ''}
                        </span>
                    </div>
                    <div className="template-status">
                        {template.isSystem && (
                            <span className="badge badge-info">System</span>
                        )}
                        {!template.isActive && (
                            <span className="badge badge-secondary">Inactive</span>
                        )}
                        {template.isPublic && (
                            <span className="badge badge-success">Public</span>
                        )}
                        {!template.isPublic && !template.isSystem && (
                            <span className="badge badge-warning">Private</span>
                        )}
                    </div>
                </div>

                <div className="template-body">
                    <p className="template-description">
                        {template.description || 'No description available'}
                    </p>
                </div>

                <div className="template-actions">
                    <button 
                        className="btn btn-sm btn-outline-info"
                        onClick={() => handlePreview(template.templateId)}
                        title="Preview Template"
                    >
                        <FaEye /> Preview
                    </button>
                    
                    {!template.isSystem && template.isOwner && (
                        <>
                            <button 
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleEdit(template.templateId)}
                                title="Edit Template"
                            >
                                <FaEdit /> Edit
                            </button>
                            
                            {template.isActive ? (
                                <button 
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleDeleteClick(template.templateId, template.name)}
                                    title="Delete Template"
                                >
                                    <FaTrash /> Delete
                                </button>
                            ) : (
                                <button 
                                    className="btn btn-sm btn-outline-success"
                                    onClick={() => handleReactivate(template.templateId, template.name)}
                                    title="Reactivate Template"
                                >
                                    <FaPlay /> Reactivate
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="template-management-page">
                <div className="loading-spinner">
                    <FaSpinner className="fa-spin fa-3x" />
                    <p>Loading board templates...</p>
                </div>
            </div>
        );
    }

    const filteredTemplates = getFilteredTemplates();

    return (
        <div className="template-management-page">
            <div className="page-header">
                <h1>
                    <FaLayerGroup /> Board Template Management
                </h1>
                <p className="page-description">
                    Design Kanban board column layouts. Board templates define the workflow stages (columns) that action items move through. Assign a default board to each module context under <strong>Module Assignments</strong> to auto-route new items.
                </p>
            </div>

            {/* Tab Navigation */}
            <div className="tab-navigation">
                <button
                    className={`tab-btn ${activeTab === 'templates' ? 'active' : ''}`}
                    onClick={() => setActiveTab('templates')}
                >
                    <FaLayerGroup /> Board Templates
                </button>
                <button
                    className={`tab-btn ${activeTab === 'boards' ? 'active' : ''}`}
                    onClick={() => setActiveTab('boards')}
                >
                    <FaClipboardList /> Board Management
                </button>
                <button
                    className={`tab-btn ${activeTab === 'tasks' ? 'active' : ''}`}
                    onClick={() => setActiveTab('tasks')}
                >
                    <FaTasks /> Task Management
                </button>
            </div>

            {/* Board Management Tab */}
            {activeTab === 'boards' && (
                <div className="board-mgmt-panel">
                    <div className="board-mgmt-toolbar">
                        <input
                            type="text"
                            className="board-mgmt-search"
                            placeholder="Search boards by name or description..."
                            value={boardSearchQuery}
                            onChange={(e) => setBoardSearchQuery(e.target.value)}
                        />
                        <select
                            className="board-mgmt-filter"
                            value={boardStatusFilter}
                            onChange={(e) => setBoardStatusFilter(e.target.value)}
                        >
                            <option value="all">All Boards</option>
                            <option value="active">Active Only</option>
                            <option value="inactive">Inactive Only</option>
                        </select>
                        <button
                            className="btn btn-primary"
                            onClick={() => setShowCreateBoardModal(true)}
                        >
                            <FaPlus /> Create Board
                        </button>
                    </div>

                    {loadingBoards ? (
                        <div className="loading-spinner">
                            <FaSpinner className="fa-spin fa-3x" />
                            <p>Loading boards...</p>
                        </div>
                    ) : filteredBoards.length === 0 ? (
                        <div className="board-mgmt-empty">
                            <FaInbox />
                            <p>No boards found</p>
                        </div>
                    ) : (
                        <table className="board-mgmt-table">
                            <thead>
                                <tr>
                                    <th>Board</th>
                                    <th>Description</th>
                                    <th>Status</th>
                                    <th>Owner</th>
                                    <th>Access</th>
                                    <th>Tasks</th>
                                    <th style={{ textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredBoards.map(board => (
                                    <tr key={board.boardId} className={!board.isActive ? 'inactive-row' : ''}>
                                        <td>
                                            <div className="board-name-cell">
                                                {board.color && <span className="board-color-dot" style={{ backgroundColor: board.color }} />}
                                                {board.icon && <span className="board-icon"><FaIcon icon={board.icon} /></span>}
                                                <span className="board-name-text">{board.name}</span>
                                            </div>
                                        </td>
                                        <td className="board-description-cell" title={board.description || ''}>
                                            {board.description || '—'}
                                        </td>
                                        <td>
                                            <div className="board-badges-cell">
                                                {board.isActive ? (
                                                    <span className="badge badge-success">Active</span>
                                                ) : (
                                                    <span className="badge badge-secondary">Inactive</span>
                                                )}
                                                {board.isPublic && <span className="badge badge-info">Public</span>}
                                                {board.isDefault && <span className="badge badge-warning">Default</span>}
                                            </div>
                                        </td>
                                        <td className="board-owner-cell">
                                            {board.ownerName || (board.isOwner ? 'You' : '—')}
                                        </td>
                                        <td>
                                            <div className="board-badges-cell" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '3px' }}>
                                                {board.accessRoles?.map((r, i) => (
                                                    <span key={`r-${i}`} className="badge" style={{ background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd', fontSize: '0.7rem' }} title={`Role: ${r.roleName} (${r.permissionLevel})`}>
                                                        <IoPeople style={{ marginRight: 3 }} size={10} />{r.roleName}
                                                    </span>
                                                ))}
                                                {board.accessDepartments?.map((d, i) => (
                                                    <span key={`d-${i}`} className="badge" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fbbf24', fontSize: '0.7rem' }} title={`Dept: ${d.departmentName} (${d.permissionLevel})`}>
                                                        <IoBusiness style={{ marginRight: 3 }} size={10} />{d.departmentName}
                                                    </span>
                                                ))}
                                                {board.accessUsers?.map((u, i) => (
                                                    <span key={`u-${i}`} className="badge" style={{ background: '#ede9fe', color: '#5b21b6', border: '1px solid #c4b5fd', fontSize: '0.7rem' }} title={`User: ${u.userName} (${u.permissionLevel})`}>
                                                        <IoPerson style={{ marginRight: 3 }} size={10} />{u.userName}
                                                    </span>
                                                ))}
                                                {(!board.accessRoles?.length && !board.accessDepartments?.length && !board.accessUsers?.length) && (
                                                    <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>No Permissions</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="board-tasks-cell">
                                            {board.actionItemCount ?? 0}
                                        </td>
                                        <td>
                                            <div className="board-actions-cell">
                                                <button
                                                    className="btn-icon-action"
                                                    title="Board Settings"
                                                    onClick={() => { setCurrentBoard(board); setShowBoardSettingsModal(true); }}
                                                >
                                                    <FaCog />
                                                </button>
                                                <button
                                                    className="btn-icon-action"
                                                    title="Manage Access"
                                                    onClick={() => { setCurrentBoard(board); setShowManageAccessModal(true); }}
                                                >
                                                    <FaUsers />
                                                </button>
                                                <button
                                                    className="btn-icon-action"
                                                    title="Duplicate Board"
                                                    onClick={() => { setCurrentBoard(board); setShowDuplicateBoardModal(true); }}
                                                >
                                                    <FaCopy />
                                                </button>
                                                <button
                                                    className="btn-icon-action danger"
                                                    title="Delete Board"
                                                    onClick={() => setConfirmDeleteBoard(board)}
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* Task Management Tab */}
            {activeTab === 'tasks' && (
                <TaskManagementPanel apiFetch={apiFetch} boards={boards} loadBoards={loadBoards} />
            )}

            {/* Templates Tab */}
            {activeTab === 'templates' && (
                <>
                    <div className="page-controls">
                        <div className="filter-controls">
                            <div className="form-group">
                                <label htmlFor="categoryFilter">Category:</label>
                                <select 
                                    id="categoryFilter"
                                    className="form-control"
                                    value={filterCategory}
                                    onChange={(e) => setFilterCategory(e.target.value)}
                                >
                                    {categories.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-check">
                                <input 
                                    type="checkbox" 
                                    className="form-check-input" 
                                    id="showInactiveCheck"
                                    checked={showInactive}
                                    onChange={(e) => setShowInactive(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="showInactiveCheck">
                                    Show Inactive Templates
                                </label>
                            </div>
                        </div>

                        <button 
                            className="btn btn-primary"
                            onClick={handleCreateNew}
                        >
                            <FaPlus /> Create New Template
                        </button>
                    </div>

                    <div className="templates-container">
                        {Object.keys(filteredTemplates).length === 0 ? (
                            <div className="no-templates">
                                <FaInbox className="fa-3x" />
                                <p>No templates found for the selected filters</p>
                            </div>
                        ) : (
                            Object.keys(filteredTemplates).sort().map(category => (
                                <div key={category} className="department-section">
                                    <h2 className="department-heading">
                                        <FaFolder /> {category}
                                        <span className="template-count">
                                            ({filteredTemplates[category].length} template{filteredTemplates[category].length !== 1 ? 's' : ''})
                                        </span>
                                    </h2>
                                    <div className="templates-grid">
                                        {filteredTemplates[category].map(template => renderTemplateCard(template))}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}

            {/* Template Editor Modal */}
            {showEditor && (
                <BoardTemplateEditor
                    template={selectedTemplate}
                    onClose={handleCloseEditor}
                    onSave={loadTemplates}
                    apiFetch={apiFetch}
                />
            )}

            {/* Board Management Modals */}
            {showCreateBoardModal && (
                <CreateBoardModal
                    onClose={() => setShowCreateBoardModal(false)}
                    onCreate={async () => {
                        await loadBoards();
                        setShowCreateBoardModal(false);
                    }}
                />
            )}

            {showBoardSettingsModal && currentBoard && (
                <BoardSettingsModal
                    board={currentBoard}
                    onClose={() => { setShowBoardSettingsModal(false); setCurrentBoard(null); }}
                    onSave={async (boardId, updates) => {
                        try {
                            await kanbanBoardService.updateBoard(apiFetch, boardId, updates);
                            toast.success('Board updated successfully');
                            setShowBoardSettingsModal(false);
                            setCurrentBoard(null);
                            await loadBoards();
                        } catch (error) {
                            Sentry.captureException(error);
                            toast.error(error.message || 'Failed to update board');
                        }
                    }}
                    onDelete={async (boardId) => {
                        try {
                            await kanbanBoardService.deleteBoard(apiFetch, boardId);
                            toast.success('Board deleted successfully');
                            setShowBoardSettingsModal(false);
                            setCurrentBoard(null);
                            await loadBoards();
                        } catch (error) {
                            Sentry.captureException(error);
                            toast.error(error.message || 'Failed to delete board');
                        }
                    }}
                    onAddColumn={async (boardId, columnData) => {
                        try {
                            await kanbanBoardService.addColumn(apiFetch, boardId, columnData);
                            toast.success('Column added successfully');
                        } catch (error) {
                            Sentry.captureException(error);
                            toast.error(error.message || 'Failed to add column');
                        }
                    }}
                    onUpdateColumn={async (boardId, columnId, updates) => {
                        try {
                            await kanbanBoardService.updateColumn(apiFetch, boardId, columnId, updates);
                            toast.success('Column updated successfully');
                        } catch (error) {
                            Sentry.captureException(error);
                            toast.error(error.message || 'Failed to update column');
                        }
                    }}
                    onDeleteColumn={async (boardId, columnId) => {
                        try {
                            await kanbanBoardService.deleteColumn(apiFetch, boardId, columnId);
                            toast.success('Column deleted successfully');
                        } catch (error) {
                            Sentry.captureException(error);
                            toast.error(error.message || 'Failed to delete column');
                        }
                    }}
                />
            )}

            {showManageAccessModal && currentBoard && (
                <ManageAccessModal
                    board={currentBoard}
                    sharedWith={currentBoard.sharedWith || []}
                    onClose={() => { setShowManageAccessModal(false); setCurrentBoard(null); }}
                    onAccessChanged={loadBoards}
                    onGrantAccess={async (accessData) => {
                        try {
                            await kanbanBoardService.grantAccess(apiFetch, currentBoard.boardId, accessData);
                            toast.success('Access granted successfully');
                            await loadBoards();
                        } catch (error) {
                            Sentry.captureException(error);
                            toast.error(error.message || 'Failed to grant access');
                        }
                    }}
                    onRevokeAccess={async (accessId) => {
                        try {
                            await kanbanBoardService.revokeAccess(apiFetch, currentBoard.boardId, accessId);
                            toast.success('Access revoked successfully');
                            await loadBoards();
                        } catch (error) {
                            Sentry.captureException(error);
                            toast.error(error.message || 'Failed to revoke access');
                        }
                    }}
                />
            )}

            {showDuplicateBoardModal && currentBoard && (
                <DuplicateBoardModal
                    board={currentBoard}
                    onClose={() => { setShowDuplicateBoardModal(false); setCurrentBoard(null); }}
                    onSuccess={async () => {
                        await loadBoards();
                    }}
                />
            )}

            {confirmDeleteBoard && (
                <div className="modal-overlay" onClick={() => setConfirmDeleteBoard(null)}>
                    <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Confirm Delete Board</h2>
                            <button className="close-button" onClick={() => setConfirmDeleteBoard(null)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete the board <strong>"{confirmDeleteBoard.name}"</strong>?</p>
                            <p className="text-warning">This action cannot be undone. All action items on this board will be affected.</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setConfirmDeleteBoard(null)}>Cancel</button>
                            <button className="btn btn-danger" onClick={() => handleDeleteBoard(confirmDeleteBoard.boardId)}>
                                <FaTrash /> Delete Board
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Template Delete Confirmation Modal */}
            {confirmDelete && (
                <div className="modal-overlay" onClick={handleDeleteCancel}>
                    <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Confirm Delete</h2>
                            <button className="close-button" onClick={handleDeleteCancel}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete the template <strong>"{confirmDelete.templateName}"</strong>?</p>
                            <p className="text-warning">This action cannot be undone.</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={handleDeleteCancel}>
                                Cancel
                            </button>
                            <button className="btn btn-danger" onClick={handleDeleteConfirm}>
                                <FaTrash /> Delete Template
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Board Template Editor Component
 * Modal dialog for creating/editing board templates
 */
const BoardTemplateEditor = ({ template, onClose, onSave, apiFetch }) => {
    const [formData, setFormData] = useState({
        name: template?.name || '',
        description: template?.description || '',
        category: template?.category || 'Custom',
        icon: template?.icon || '',
        color: template?.color || '#3498db',
        isPublic: template?.isPublic || false,
        columns: template?.columns || []
    });
    const [saving, setSaving] = useState(false);
    const isPreviewMode = template?.previewMode === true;

    /**
     * Handle form field changes
     */
    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    /**
     * Handle column changes
     */
    const handleColumnChange = (index, field, value) => {
        const updatedColumns = [...formData.columns];
        updatedColumns[index] = { ...updatedColumns[index], [field]: value };
        setFormData(prev => ({ ...prev, columns: updatedColumns }));
    };

    /**
     * Add a new column
     */
    const handleAddColumn = () => {
        const newColumn = {
            name: `Column ${formData.columns.length + 1}`,
            statusValue: 'Open',
            color: '#3498db',
            sortOrder: formData.columns.length,
            allowCreate: true,
            isCompletedColumn: false,
            wipLimit: 0
        };
        setFormData(prev => ({ ...prev, columns: [...prev.columns, newColumn] }));
    };

    /**
     * Remove a column
     */
    const handleRemoveColumn = (index) => {
        const updatedColumns = formData.columns.filter((_, i) => i !== index);
        // Update sort orders
        updatedColumns.forEach((col, i) => col.sortOrder = i);
        setFormData(prev => ({ ...prev, columns: updatedColumns }));
    };

    /**
     * Move column up
     */
    const handleMoveColumnUp = (index) => {
        if (index === 0) return;
        const updatedColumns = [...formData.columns];
        [updatedColumns[index - 1], updatedColumns[index]] = [updatedColumns[index], updatedColumns[index - 1]];
        updatedColumns.forEach((col, i) => col.sortOrder = i);
        setFormData(prev => ({ ...prev, columns: updatedColumns }));
    };

    /**
     * Move column down
     */
    const handleMoveColumnDown = (index) => {
        if (index === formData.columns.length - 1) return;
        const updatedColumns = [...formData.columns];
        [updatedColumns[index], updatedColumns[index + 1]] = [updatedColumns[index + 1], updatedColumns[index]];
        updatedColumns.forEach((col, i) => col.sortOrder = i);
        setFormData(prev => ({ ...prev, columns: updatedColumns }));
    };

    /**
     * Handle save
     */
    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error('Template name is required');
            return;
        }

        if (formData.columns.length === 0) {
            toast.error('Template must have at least one column');
            return;
        }

        setSaving(true);
        try {
            const templateData = {
                name: formData.name,
                description: formData.description || null,
                category: formData.category || 'Custom',
                icon: formData.icon || null,
                color: formData.color || null,
                isPublic: formData.isPublic,
                columnsJson: JSON.stringify(formData.columns)
            };

            if (template && !isPreviewMode) {
                // Update existing template
                await updateBoardTemplate(apiFetch, template.templateId, templateData);
                toast.success('Template updated successfully');
            } else {
                // Create new template
                await createBoardTemplate(apiFetch, templateData);
                toast.success('Template created successfully');
            }

            onSave();
            onClose();
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving template:', error);
            toast.error(error.message || 'Failed to save template');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-large board-template-editor" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>
                        {isPreviewMode ? 'Preview Template' : template ? 'Edit Board Template' : 'Create Board Template'}
                    </h2>
                    <button className="close-button" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <div className="modal-body">
                    {/* Basic Information */}
                    <div className="form-section">
                        <h3>Basic Information</h3>
                        <div className="form-row">
                            <div className="form-group flex-2">
                                <label htmlFor="templateName">Template Name *</label>
                                <input
                                    id="templateName"
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    placeholder="e.g., Software Development, Marketing Campaign"
                                    disabled={isPreviewMode}
                                    maxLength={200}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="templateCategory">Category</label>
                                <input
                                    id="templateCategory"
                                    type="text"
                                    value={formData.category}
                                    onChange={(e) => handleChange('category', e.target.value)}
                                    placeholder="e.g., Development, Marketing"
                                    disabled={isPreviewMode}
                                    maxLength={100}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="templateDescription">Description</label>
                            <textarea
                                id="templateDescription"
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                placeholder="Describe the purpose and use case for this template"
                                rows={3}
                                disabled={isPreviewMode}
                                maxLength={1000}
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="templateIcon">Icon</label>
                                {isPreviewMode ? (
                                    <input
                                        id="templateIcon"
                                        type="text"
                                        value={formData.icon}
                                        disabled
                                    />
                                ) : (
                                    <IconPickerGrid
                                        value={formData.icon}
                                        onChange={(iconValue) => handleChange('icon', iconValue)}
                                    />
                                )}
                            </div>
                            <div className="form-group">
                                <label htmlFor="templateColor">Color</label>
                                <input
                                    id="templateColor"
                                    type="color"
                                    value={formData.color}
                                    onChange={(e) => handleChange('color', e.target.value)}
                                    disabled={isPreviewMode}
                                />
                            </div>
                            <div className="form-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={formData.isPublic}
                                        onChange={(e) => handleChange('isPublic', e.target.checked)}
                                        disabled={isPreviewMode}
                                    />
                                    Make template public (available to all users)
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Columns Configuration */}
                    <div className="form-section">
                        <div className="section-header">
                            <h3>Columns Configuration</h3>
                            {!isPreviewMode && (
                                <button className="btn btn-sm btn-primary" onClick={handleAddColumn}>
                                    <FaPlus /> Add Column
                                </button>
                            )}
                        </div>

                        {formData.columns.length === 0 ? (
                            <div className="empty-state">
                                <p>No columns defined. Click "Add Column" to get started.</p>
                            </div>
                        ) : (
                            <div className="columns-list">
                                {formData.columns.map((column, index) => (
                                    <div key={index} className="column-editor" style={{ borderLeftColor: column.color }}>
                                        <div className="column-header">
                                            <span className="column-order">#{index + 1}</span>
                                            <input
                                                type="text"
                                                value={column.name}
                                                onChange={(e) => handleColumnChange(index, 'name', e.target.value)}
                                                placeholder="Column name"
                                                className="column-name-input"
                                                disabled={isPreviewMode}
                                                maxLength={100}
                                            />
                                            {!isPreviewMode && (
                                                <div className="column-actions">
                                                    <button 
                                                        className="btn-icon" 
                                                        onClick={() => handleMoveColumnUp(index)}
                                                        disabled={index === 0}
                                                        title="Move Up"
                                                    >
                                                        ↑
                                                    </button>
                                                    <button 
                                                        className="btn-icon" 
                                                        onClick={() => handleMoveColumnDown(index)}
                                                        disabled={index === formData.columns.length - 1}
                                                        title="Move Down"
                                                    >
                                                        ↓
                                                    </button>
                                                    <button 
                                                        className="btn-icon btn-danger" 
                                                        onClick={() => handleRemoveColumn(index)}
                                                        title="Remove Column"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <div className="column-fields">
                                            <div className="field">
                                                <label>Status Value:</label>
                                                <select
                                                    value={column.statusValue}
                                                    onChange={(e) => handleColumnChange(index, 'statusValue', e.target.value)}
                                                    disabled={isPreviewMode}
                                                >
                                                    <option value="Open">Open</option>
                                                    <option value="Pending">Pending</option>
                                                    <option value="On-hold">On-hold</option>
                                                    <option value="Solved">Solved</option>
                                                    <option value="Closed">Closed</option>
                                                </select>
                                            </div>
                                            <div className="field">
                                                <label>Color:</label>
                                                <input
                                                    type="color"
                                                    value={column.color || '#3498db'}
                                                    onChange={(e) => handleColumnChange(index, 'color', e.target.value)}
                                                    disabled={isPreviewMode}
                                                />
                                            </div>
                                            <div className="field">
                                                <label>WIP Limit:</label>
                                                <input
                                                    type="number"
                                                    value={column.wipLimit || 0}
                                                    onChange={(e) => handleColumnChange(index, 'wipLimit', parseInt(e.target.value, 10) || 0)}
                                                    min="0"
                                                    disabled={isPreviewMode}
                                                />
                                            </div>
                                            <div className="field checkbox-field">
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={column.allowCreate}
                                                        onChange={(e) => handleColumnChange(index, 'allowCreate', e.target.checked)}
                                                        disabled={isPreviewMode}
                                                    />
                                                    Allow Create
                                                </label>
                                            </div>
                                            <div className="field checkbox-field">
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={column.isCompletedColumn}
                                                        onChange={(e) => handleColumnChange(index, 'isCompletedColumn', e.target.checked)}
                                                        disabled={isPreviewMode}
                                                    />
                                                    Completed Column
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="btn-secondary" onClick={onClose} disabled={saving}>
                        {isPreviewMode ? 'Close' : 'Cancel'}
                    </button>
                    {!isPreviewMode && (
                        <button className="btn-primary" onClick={handleSave} disabled={saving}>
                            {saving ? (
                                <>
                                    <FaSpinner className="fa-spin" /> Saving...
                                </>
                            ) : (
                                <>
                                    <FaSave /> Save Template
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * Task Management Panel Component
 * Provides filtered bulk migration and bulk update of action items
 */
const TaskManagementPanel = ({ apiFetch, boards, loadBoards }) => {
    const [users, setUsers] = useState([]);
    const [properties, setProperties] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loadingRef, setLoadingRef] = useState(true);

    // Filter state
    const [filters, setFilters] = useState({
        boardId: '',
        assignedToUserId: '',
        propertyId: '',
        status: '',
        priority: '',
        actionItemsType: '',
        dateFrom: '',
        dateTo: '',
        assignedToDepartmentId: '',
        assignedToFunctionId: '',
        templateKey: '',
        tags: ''
    });

    // Preview
    const [matchCount, setMatchCount] = useState(null);
    const [previewItems, setPreviewItems] = useState([]);
    const [previewLimit, setPreviewLimit] = useState(200);
    const [counting, setCounting] = useState(false);

    // Action mode
    const [actionMode, setActionMode] = useState(''); // 'migrate' | 'update'
    const [executing, setExecuting] = useState(false);
    const [jobStatus, setJobStatus] = useState(null); // async job progress

    // Migrate target
    const [targetBoardId, setTargetBoardId] = useState('');

    // Bulk update fields
    const [updateFields, setUpdateFields] = useState({
        newStatus: '',
        newPriority: '',
        newAssignedToUserId: '',
        newAssignedToDepartmentId: '',
        newAssignedToFunctionId: '',
        newAssignedToPropertyId: '',
        newBoardId: '',
        newDueDate: '',
        clearDueDate: false,
        newTitle: '',
        newDescription: '',
        newTags: '',
        newActionItemsType: '',
        newSource: ''
    });

    // Load reference data
    useEffect(() => {
        const loadRefData = async () => {
            setLoadingRef(true);
            try {
                const [usersRes, propsRes, deptsRes] = await Promise.all([
                    apiFetch('/api/User/all'),
                    apiFetch('/api/Property/all'),
                    apiFetch('/api/Department')
                ]);
                const usersData = await usersRes.json();
                const propsData = await propsRes.json();
                const deptsData = await deptsRes.json();
                if (usersData.success) setUsers(usersData.data || []);
                if (propsData.success) setProperties(propsData.data || []);
                if (deptsData.success) setDepartments(deptsData.data || []);
            } catch (error) {
                Sentry.captureException(error);
                toast.error('Failed to load reference data');
            } finally {
                setLoadingRef(false);
            }
        };
        loadRefData();
    }, [apiFetch]);

    const buildFilterPayload = () => {
        const f = {};
        if (filters.boardId === 'unboarded') f.filterUnboarded = true;
        else if (filters.boardId) f.boardId = parseInt(filters.boardId, 10);
        if (filters.assignedToUserId) f.assignedToUserId = parseInt(filters.assignedToUserId, 10);
        if (filters.propertyId) f.propertyId = parseInt(filters.propertyId, 10);
        if (filters.status) f.status = filters.status;
        if (filters.priority) f.priority = filters.priority;
        if (filters.actionItemsType) f.actionItemsType = filters.actionItemsType;
        if (filters.dateFrom) f.dateFrom = filters.dateFrom;
        if (filters.dateTo) f.dateTo = filters.dateTo;
        if (filters.assignedToDepartmentId) f.assignedToDepartmentId = parseInt(filters.assignedToDepartmentId, 10);
        if (filters.assignedToFunctionId) f.assignedToFunctionId = parseInt(filters.assignedToFunctionId, 10);
        if (filters.templateKey) f.templateKey = filters.templateKey;
        if (filters.tags) f.tags = filters.tags;
        return f;
    };

    const hasAnyFilter = Object.values(filters).some(v => v !== '' && v !== false);

    const handlePreviewCount = async () => {
        if (!hasAnyFilter) {
            toast.warning('Please set at least one filter before previewing');
            return;
        }
        setCounting(true);
        try {
            const result = await kanbanBoardService.getFilteredTaskCount(apiFetch, buildFilterPayload());
            setMatchCount(result.count);
            setPreviewItems(result.preview || []);
            setPreviewLimit(result.previewLimit || 200);
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to get count');
        } finally {
            setCounting(false);
        }
    };

    const pollJob = async (jobId, actionLabel) => {
        setJobStatus({ status: 'Queued', message: `${actionLabel} queued...` });
        try {
            const finalStatus = await kanbanBoardService.pollBulkJob(
                apiFetch, jobId,
                (s) => setJobStatus(s)
            );
            if (finalStatus.status === 'Completed') {
                toast.success(finalStatus.message);
            } else {
                toast.error(finalStatus.message || `${actionLabel} failed`);
            }
            setMatchCount(null);
            setPreviewItems([]);
            setActionMode('');
            await loadBoards();
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || `${actionLabel} failed`);
        } finally {
            setJobStatus(null);
            setExecuting(false);
        }
    };

    const handleMigrate = async () => {
        if (!targetBoardId) {
            toast.error('Please select a target board');
            return;
        }
        setExecuting(true);
        try {
            const result = await kanbanBoardService.bulkMigrateFiltered(
                apiFetch, buildFilterPayload(), parseInt(targetBoardId, 10)
            );
            await pollJob(result.jobId, 'Migration');
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to queue migration');
            setExecuting(false);
        }
    };

    const handleBulkUpdate = async () => {
        const updates = {};
        if (updateFields.newStatus) updates.newStatus = updateFields.newStatus;
        if (updateFields.newPriority) updates.newPriority = updateFields.newPriority;
        if (updateFields.newAssignedToUserId) updates.newAssignedToUserId = parseInt(updateFields.newAssignedToUserId, 10);
        if (updateFields.newAssignedToDepartmentId) updates.newAssignedToDepartmentId = parseInt(updateFields.newAssignedToDepartmentId, 10);
        if (updateFields.newAssignedToFunctionId) updates.newAssignedToFunctionId = parseInt(updateFields.newAssignedToFunctionId, 10);
        if (updateFields.newAssignedToPropertyId) updates.newAssignedToPropertyId = parseInt(updateFields.newAssignedToPropertyId, 10);
        if (updateFields.newBoardId) updates.newBoardId = parseInt(updateFields.newBoardId, 10);
        if (updateFields.newDueDate) updates.newDueDate = updateFields.newDueDate;
        if (updateFields.clearDueDate) updates.clearDueDate = true;
        if (updateFields.newTitle) updates.newTitle = updateFields.newTitle;
        if (updateFields.newDescription) updates.newDescription = updateFields.newDescription;
        if (updateFields.newTags) updates.newTags = updateFields.newTags;
        if (updateFields.newActionItemsType) updates.newActionItemsType = updateFields.newActionItemsType;
        if (updateFields.newSource) updates.newSource = updateFields.newSource;

        if (Object.keys(updates).length === 0) {
            toast.error('Please set at least one field to update');
            return;
        }
        setExecuting(true);
        try {
            const result = await kanbanBoardService.bulkUpdateTasks(apiFetch, buildFilterPayload(), updates);
            await pollJob(result.jobId, 'Bulk update');
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to queue update');
            setExecuting(false);
        }
    };

    const handleClearFilters = () => {
        setFilters({
            boardId: '', assignedToUserId: '', propertyId: '',
            status: '', priority: '', actionItemsType: '', dateFrom: '', dateTo: '',
            assignedToDepartmentId: '', assignedToFunctionId: '', templateKey: '', tags: ''
        });
        setMatchCount(null);
        setPreviewItems([]);
        setActionMode('');
    };

    if (loadingRef) {
        return (
            <div className="loading-spinner">
                <FaSpinner className="fa-spin fa-3x" />
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <div className="task-mgmt-panel">
            <div className="task-mgmt-description">
                <p>Use filters to select action items, then choose an action to perform on all matching items. Operations run in the background.</p>
            </div>

            {/* Job Progress Banner */}
            {jobStatus && (
                <div className={`job-status-banner ${jobStatus.status?.toLowerCase()}`}>
                    <FaSpinner className="fa-spin" />
                    <span>{jobStatus.message || `Job ${jobStatus.status}...`}</span>
                    {jobStatus.affectedCount > 0 && <span className="affected-count">{jobStatus.affectedCount} item(s) affected</span>}
                </div>
            )}

            {/* Filter Section */}
            <div className="task-mgmt-filters">
                <h3><FaFilter /> Filters</h3>
                <div className="filter-grid">
                    <div className="filter-field">
                        <label>Board</label>
                        <select value={filters.boardId} onChange={(e) => setFilters(f => ({ ...f, boardId: e.target.value }))}>
                            <option value="">All Boards</option>
                            <option value="unboarded">Unboarded (no board assigned)</option>
                            {boards.map(b => (
                                <option key={b.boardId} value={b.boardId}>{b.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-field">
                        <label>Assigned To</label>
                        <select value={filters.assignedToUserId} onChange={(e) => setFilters(f => ({ ...f, assignedToUserId: e.target.value }))}>
                            <option value="">All Users</option>
                            {users.map(u => (
                                <option key={u.id} value={u.id}>
                                    {u.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-field">
                        <label>Department</label>
                        <select value={filters.assignedToDepartmentId} onChange={(e) => setFilters(f => ({ ...f, assignedToDepartmentId: e.target.value }))}>
                            <option value="">All Departments</option>
                            {departments.map(d => (
                                <option key={d.departmentID} value={d.departmentID}>
                                    {d.departmentName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-field">
                        <label>Property</label>
                        <select value={filters.propertyId} onChange={(e) => setFilters(f => ({ ...f, propertyId: e.target.value }))}>
                            <option value="">All Properties</option>
                            {properties.map(p => (
                                <option key={p.propertyID} value={p.propertyID}>
                                    {p.propertyName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-field">
                        <label>Status</label>
                        <select value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}>
                            <option value="">All Statuses</option>
                            <option value="Open">Open</option>
                            <option value="Pending">Pending</option>
                            <option value="On-hold">On-hold</option>
                            <option value="Solved">Solved</option>
                            <option value="Closed">Closed</option>
                        </select>
                    </div>
                    <div className="filter-field">
                        <label>Priority</label>
                        <select value={filters.priority} onChange={(e) => setFilters(f => ({ ...f, priority: e.target.value }))}>
                            <option value="">All Priorities</option>
                            <option value="Low">Low</option>
                            <option value="Medium">Medium</option>
                            <option value="High">High</option>
                            <option value="Critical">Critical</option>
                        </select>
                    </div>
                    <div className="filter-field">
                        <label>Type</label>
                        <select value={filters.actionItemsType} onChange={(e) => setFilters(f => ({ ...f, actionItemsType: e.target.value }))}>
                            <option value="">All Types</option>
                            <option value="Non-Compliance">Non-Compliance</option>
                            <option value="SIPA">SIPA</option>
                            <option value="Manual">Manual</option>
                            <option value="IT">IT</option>
                        </select>
                    </div>
                    <div className="filter-field">
                        <label>Template Key</label>
                        <input type="text" placeholder="e.g. NonCompliance" value={filters.templateKey} onChange={(e) => setFilters(f => ({ ...f, templateKey: e.target.value }))} />
                    </div>
                    <div className="filter-field">
                        <label>Tags (contains)</label>
                        <input type="text" placeholder="e.g. urgent" value={filters.tags} onChange={(e) => setFilters(f => ({ ...f, tags: e.target.value }))} />
                    </div>
                    <div className="filter-field">
                        <label>Created From</label>
                        <input type="date" value={filters.dateFrom} onChange={(e) => setFilters(f => ({ ...f, dateFrom: e.target.value }))} />
                    </div>
                    <div className="filter-field">
                        <label>Created To</label>
                        <input type="date" value={filters.dateTo} onChange={(e) => setFilters(f => ({ ...f, dateTo: e.target.value }))} />
                    </div>
                </div>
                <div className="filter-actions">
                    <button className="btn btn-primary" onClick={handlePreviewCount} disabled={counting || !hasAnyFilter}>
                        {counting ? <><FaSpinner className="fa-spin" /> Counting...</> : <><FaSearch /> Preview Count</>}
                    </button>
                    <button className="btn-secondary" onClick={handleClearFilters}>
                        Clear Filters
                    </button>
                    {matchCount !== null && (
                        <span className="match-count-badge">
                            {matchCount} matching task{matchCount !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>
            </div>

            {/* Preview Table */}
            {previewItems.length > 0 && (
                <div className="task-mgmt-preview">
                    <h3><FaClipboardList /> Preview {matchCount > previewLimit ? `(showing first ${previewLimit} of ${matchCount})` : `(${matchCount} item${matchCount !== 1 ? 's' : ''})`}</h3>
                    <div className="preview-table-wrapper" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <table className="preview-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>ID</th>
                                    <th style={{ textAlign: 'left' }}>Title</th>
                                    <th style={{ textAlign: 'left' }}>Status</th>
                                    <th style={{ textAlign: 'left' }}>Priority</th>
                                    <th style={{ textAlign: 'left' }}>Type</th>
                                    <th style={{ textAlign: 'left' }}>Assigned To</th>
                                    <th style={{ textAlign: 'left' }}>Board</th>
                                </tr>
                            </thead>
                            <tbody>
                                {previewItems.map(item => {
                                    const assignedUser = users.find(u => u.id === item.assignedToUserID);
                                    const board = boards.find(b => b.boardId === item.boardId);
                                    return (
                                        <tr key={item.id}>
                                            <td>
                                                <a href={`/action-items/${item.id}`} target="_blank" rel="noopener noreferrer">
                                                    #{item.id}
                                                </a>
                                            </td>
                                            <td>
                                                <a href={`/action-items/${item.id}`} target="_blank" rel="noopener noreferrer">
                                                    {item.title || '(untitled)'}
                                                </a>
                                            </td>
                                            <td>{item.status}</td>
                                            <td>{item.priority}</td>
                                            <td>{item.type || '—'}</td>
                                            <td>{assignedUser ? assignedUser.name : (item.assignedToUserID ? `User #${item.assignedToUserID}` : '—')}</td>
                                            <td>{board ? board.name : (item.boardId ? `Board #${item.boardId}` : <em>Unboarded</em>)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Action Section */}
            {matchCount !== null && matchCount > 0 && (
                <div className="task-mgmt-actions">
                    <h3>Choose Action</h3>
                    <div className="action-buttons">
                        <button
                            className={`btn ${actionMode === 'migrate' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setActionMode('migrate')}
                            disabled={executing}
                        >
                            <FaExchangeAlt /> Migrate to Board
                        </button>
                        <button
                            className={`btn ${actionMode === 'update' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setActionMode('update')}
                            disabled={executing}
                        >
                            <FaSyncAlt /> Bulk Update
                        </button>
                    </div>

                    {/* Migrate Action */}
                    {actionMode === 'migrate' && (
                        <div className="action-panel">
                            <h4>Migrate {matchCount} task(s) to:</h4>
                            <div className="action-fields">
                                <select value={targetBoardId} onChange={(e) => setTargetBoardId(e.target.value)}>
                                    <option value="">-- Select target board --</option>
                                    {boards.filter(b => b.isActive && b.boardId !== parseInt(filters.boardId, 10))
                                        .map(b => (
                                            <option key={b.boardId} value={b.boardId}>{b.name}</option>
                                        ))}
                                </select>
                            </div>
                            <button className="btn btn-primary" onClick={handleMigrate} disabled={executing || !targetBoardId}>
                                {executing ? <><FaSpinner className="fa-spin" /> Processing...</> : <><FaExchangeAlt /> Migrate Tasks</>}
                            </button>
                        </div>
                    )}

                    {/* Bulk Update Action */}
                    {actionMode === 'update' && (
                        <div className="action-panel">
                            <h4>Update {matchCount} task(s) — set new values (leave blank for no change):</h4>
                            <div className="action-fields">
                                <div className="update-field">
                                    <label>Status</label>
                                    <select value={updateFields.newStatus} onChange={(e) => setUpdateFields(f => ({ ...f, newStatus: e.target.value }))}>
                                        <option value="">— No change —</option>
                                        <option value="Open">Open</option>
                                        <option value="Pending">Pending</option>
                                        <option value="On-hold">On-hold</option>
                                        <option value="Solved">Solved</option>
                                        <option value="Closed">Closed</option>
                                    </select>
                                </div>
                                <div className="update-field">
                                    <label>Priority</label>
                                    <select value={updateFields.newPriority} onChange={(e) => setUpdateFields(f => ({ ...f, newPriority: e.target.value }))}>
                                        <option value="">— No change —</option>
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="High">High</option>
                                        <option value="Critical">Critical</option>
                                    </select>
                                </div>
                                <div className="update-field">
                                    <label>Assign To User</label>
                                    <select value={updateFields.newAssignedToUserId} onChange={(e) => setUpdateFields(f => ({ ...f, newAssignedToUserId: e.target.value }))}>
                                        <option value="">— No change —</option>
                                        {users.map(u => (
                                            <option key={u.id} value={u.id}>
                                                {u.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="update-field">
                                    <label>Assign To Department</label>
                                    <select value={updateFields.newAssignedToDepartmentId} onChange={(e) => setUpdateFields(f => ({ ...f, newAssignedToDepartmentId: e.target.value }))}>
                                        <option value="">— No change —</option>
                                        {departments.map(d => (
                                            <option key={d.departmentID} value={d.departmentID}>
                                                {d.departmentName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="update-field">
                                    <label>Assign To Property</label>
                                    <select value={updateFields.newAssignedToPropertyId} onChange={(e) => setUpdateFields(f => ({ ...f, newAssignedToPropertyId: e.target.value }))}>
                                        <option value="">— No change —</option>
                                        {properties.map(p => (
                                            <option key={p.propertyID} value={p.propertyID}>
                                                {p.propertyName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="update-field">
                                    <label>Move to Board</label>
                                    <select value={updateFields.newBoardId} onChange={(e) => setUpdateFields(f => ({ ...f, newBoardId: e.target.value }))}>
                                        <option value="">— No change —</option>
                                        {boards.filter(b => b.isActive).map(b => (
                                            <option key={b.boardId} value={b.boardId}>{b.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="update-field">
                                    <label>Due Date</label>
                                    <input type="date" value={updateFields.newDueDate} onChange={(e) => setUpdateFields(f => ({ ...f, newDueDate: e.target.value, clearDueDate: false }))} />
                                    <label className="checkbox-inline">
                                        <input type="checkbox" checked={updateFields.clearDueDate} onChange={(e) => setUpdateFields(f => ({ ...f, clearDueDate: e.target.checked, newDueDate: '' }))} />
                                        Clear due date
                                    </label>
                                </div>
                                <div className="update-field">
                                    <label>Type</label>
                                    <select value={updateFields.newActionItemsType} onChange={(e) => setUpdateFields(f => ({ ...f, newActionItemsType: e.target.value }))}>
                                        <option value="">— No change —</option>
                                        <option value="Non-Compliance">Non-Compliance</option>
                                        <option value="SIPA">SIPA</option>
                                        <option value="Manual">Manual</option>
                                        <option value="IT">IT</option>
                                    </select>
                                </div>
                                <div className="update-field">
                                    <label>Tags</label>
                                    <input type="text" placeholder="Comma-separated tags" value={updateFields.newTags} onChange={(e) => setUpdateFields(f => ({ ...f, newTags: e.target.value }))} />
                                </div>
                                <div className="update-field">
                                    <label>Title</label>
                                    <input type="text" placeholder="New title" value={updateFields.newTitle} onChange={(e) => setUpdateFields(f => ({ ...f, newTitle: e.target.value }))} />
                                </div>
                                <div className="update-field update-field-wide">
                                    <label>Description</label>
                                    <textarea placeholder="New description" value={updateFields.newDescription} onChange={(e) => setUpdateFields(f => ({ ...f, newDescription: e.target.value }))} rows={2} />
                                </div>
                            </div>
                            <button className="btn btn-primary" onClick={handleBulkUpdate} disabled={executing}>
                                {executing ? <><FaSpinner className="fa-spin" /> Processing...</> : <><FaSyncAlt /> Apply Updates</>}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BoardTemplateManagementPage;
